<script lang="ts" setup>
import type { IProps } from './RecommendationCards.types'
import { formatDate } from '@/utils/date'

defineProps<IProps>()
const model = defineModel<number>()

const recommendationIcons = {
  Kupuj: 'icon-kupuj.png',
  Trzymaj: 'icon-trzymaj.png',
  Sprzedaj: 'icon-sprzedaj.png',
} as const

function formatPrice(value: number) {
  return new Intl.NumberFormat('pl-PL', {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}
</script>

<template>
  <div class="investor-stock-recommendation-cards">
    <div class="investor-stock-recommendation-cards__container">
      <h2 class="investor-stock-recommendation-cards__title">
        {{ $t('investor.stock.recommendation.table.mainHeading') }}
      </h2>

      <div class="investor-stock-recommendation-cards__cards">
        <div
          v-for="(item, index) in items"
          :key="`recommendation-card-${index}`"
          class="investor-stock-recommendation-cards__card"
        >
          <p class="investor-stock-recommendation-cards__date">
            {{ formatDate(item.date) }}
          </p>

          <div class="investor-stock-recommendation-cards__row">
            <p class="investor-stock-recommendation-cards__institution">
              {{ item.institution }}
            </p>

            <div class="investor-stock-recommendation-cards__recommendation">
              <img
                :src="`/icons/investor/recommendation/${recommendationIcons[item.recommendation as keyof typeof recommendationIcons]}`"
                aria-hidden="true"
              >

              {{ $t(`investor.stock.recommendation.table.recommendation.${item.recommendation.toLowerCase()}`) }}
            </div>
          </div>

          <hr class="investor-stock-recommendation-cards__divider">

          <div class="investor-stock-recommendation-cards__row">
            <div class="investor-stock-recommendation-cards__price">
              <span class="investor-stock-recommendation-cards__price-label">
                {{ $t('investor.stock.recommendation.table.heading3') }}
              </span>

              <span class="investor-stock-recommendation-cards__price-value">
                {{ formatPrice(item.targetPrice) }}
              </span>
            </div>

            <div class="investor-stock-recommendation-cards__price">
              <span class="investor-stock-recommendation-cards__price-label">
                {{ $t('investor.stock.recommendation.table.heading4') }}
              </span>

              <span class="investor-stock-recommendation-cards__price-value">
                {{ formatPrice(item.issuePrice) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <UiPagination
        v-model="model"
        class="investor-stock-recommendation-cards__pagination"
        :page-count="Math.ceil(total / itemsPerPage)"
        :total="total"
        :items-per-page="itemsPerPage"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.investor-stock-recommendation-cards {
  @include section-investor();

  &__container {
    @include section-investor__container();
  }

  &__title {
    @include h5;
    margin-bottom: 3.2rem;
    color: $text-400;
    text-align: center;
  }

  &__cards {
    display: grid;
    gap: 1.2rem;
  }

  &__card {
    padding: 1.6rem 2.4rem;
    border: 1px solid $gray-300;
    border-radius: 1.2rem;
  }

  &__divider {
    height: 1px;
    margin: 0.8rem 0;
    background-color: $base-50;
  }

  &__date {
    @include body-3;
    margin-bottom: 0.4rem;
    color: $text-300;
  }

  &__institution {
    @include body-3-medium;
    color: $text-400;
  }

  &__recommendation {
    @include body-3;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: $text-400;
  }

  &__row {
    display: grid;
    justify-content: space-between;
    grid-template-columns: 1fr 1fr;
  }

  &__price {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  &__price-label {
    @include body-4;
    color: $text-300;
  }

  &__price-value {
    @include body-3-medium;
    color: $text-400;
  }

  &__pagination {
    justify-content: center;
    margin-top: 3.2rem;
  }
}
</style>
